import React, { useState } from "react";
import {
  Grid,
  Divider,
  Button,
  Hidden,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles"; // 👈 import this
import * as Styles from "./styles/MyAccountContainerStyles";
import BidderDetailsList from "./BidderDetailsList";
import BusinessDetailsList from "./BusinessDetailsList";
import AccountRegisteredWithDetails from "./AccountRegisteredWithDetails";
import PaymentDetails from "./PaymentDetails";
import SuspensionDetails from "./SuspensionDetails";
import { AppContext } from "../../contexts/AppContext";
import { NotificationPreferencesContainer } from "./NotificationPreferencesContainer";
import { UpdateBidderMailingList } from "../../services/BidderService";
import { useSnackbar } from "notistack";
import { useMyMember } from "../../services/MemberService";
import { LoadingComponent } from "../loading/LoadingComponent";
import { LoginHistory } from "./LoginHistory";
import { BiddingPreferences } from "./BiddingPreferences";
import { useHistory, useLocation } from "react-router";

interface MyAccountState {
  tabIndex?: string;
}

// 👇 new style for bigger scroll buttons
const useTabStyles = makeStyles({
  tabScrollButton: {
    width: 48, // make button wider
    height: 48, // make button taller
    "& svg": {
      fontSize: "2rem", // bigger arrow icons
    },
  },
});

const MyAccountContainer: React.FC = () => {
  const classes = Styles.MyAccountContainerStyles();
  const tabClasses = useTabStyles(); // 👈 use new styles here

  const [openNotificationPreferences, setOpenNotificationPreferences] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const context = React.useContext(AppContext);
  const location = useLocation<MyAccountState>();
  const history = useHistory();

  const [processing, setProcessing] = React.useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<string>("my-details");

  const [member, memberLoading] = useMyMember(() => {
    enqueueSnackbar("There was a problem loading member details, please try again.", {
      variant: "error",
    });
  });

  React.useEffect(() => {
    if (location.state?.tabIndex) {
      setTabIndex(location.state.tabIndex);
      history.replace(location.pathname); // clear the state so refresh doesn’t reset tab
    }
  }, [location.state]);

  const handleMailingListClicked = async (value: boolean) => {
    setProcessing(true);
    try {
      await UpdateBidderMailingList(value);
      context.bidderState.bidder.mailingListOptIn = value;
      enqueueSnackbar("Marketing Preferences Updated", { variant: "success" });
    } catch {
      enqueueSnackbar(
        "There was a problem changing your marketing preferences. Please try again.",
        { variant: "error" }
      );
    } finally {
      setProcessing(false);
    }
  };

  const mailToLink = "mailto:" + member?.email;

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTabIndex(newValue);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} className={classes.titleContainer}>
          <Typography variant="h4" component="h4" className={classes.titleText}>
            My Account
          </Typography>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12} lg={12}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="on"
            classes={{ scrollButtons: tabClasses.tabScrollButton }} // 👈 applied here
          >
            <Tab label="My Details" value={"my-details"} />
            {context.bidderState.bidder.business && (
              <Tab label="Account Details" value={"account-details"} />
            )}
            <Tab label="Registered With" value={"registerd-with"} />
            <Tab label="Marketing & Notifications" value={"marketing-and-notifications"} />
            <Tab label="Bidding" value={"bidding"} />
            <Tab label="Login History" value={"login-history"} />
            <Tab label="Suspensions" value={"suspensions"} />
            <Tab label="Payments" value={"payments"} />
          </Tabs>
        </Grid>

        <Grid item xs={12} className={classes.infoContainer}>
          {tabIndex === "my-details" && (
            <>
              <BidderDetailsList bidder={context.bidderState.bidder} />
              <h4 className={classes.italicText}>
                If you need to change or update any of your personal details, please contact your
                account holder below. Additional identity checks may be required.
              </h4>
              {memberLoading && member === null && (
                <CircularProgress color="primary" size={16} thickness={8} />
              )}
              {!memberLoading && member !== null && (
                <Button
                  href={mailToLink}
                  className={classes.button}
                  variant="contained"
                  color="primary"
                >
                  Request change
                </Button>
              )}
            </>
          )}

          {tabIndex === "account-details" && context.bidderState.bidder.business && (
            <>
              <BusinessDetailsList
                business={context.bidderState.bidder.business}
                bidder={context.bidderState.bidder}
              />
            </>
          )}

          {tabIndex === "registerd-with" && (
            <>
              {memberLoading && member === null && (
                <LoadingComponent label="Loading member details" />
              )}
              {!memberLoading && member !== null && (
                <AccountRegisteredWithDetails member={member} />
              )}
            </>
          )}

          {tabIndex === "marketing-and-notifications" && (
            <>
              <h4 className={classes.italicText}>
                Notification preferences allow you to customise when and how different notifications
                are sent.
              </h4>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setOpenNotificationPreferences(!openNotificationPreferences);
                }}
              >
                {openNotificationPreferences
                  ? "Hide Notification Preferences"
                  : "Manage Notifications"}
              </Button>
              {openNotificationPreferences && <NotificationPreferencesContainer />}
              <h4 className={classes.italicText}>
                Opt in or opt out of receiving marketing and promotional emails
              </h4>
              {context.bidderState.bidder.mailingListOptIn !== undefined ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      value={context.bidderState.bidder.mailingListOptIn}
                      checked={context.bidderState.bidder.mailingListOptIn}
                      onChange={(e) => handleMailingListClicked(e.target.checked)}
                      disabled={processing}
                      name="mailingListOptIn"
                      color="primary"
                    />
                  }
                  label="Opt In to receive Marketing Communications"
                />
              ) : (
                <CircularProgress color="primary" size={24} thickness={8} />
              )}
            </>
          )}

          {tabIndex === "bidding" && (
            <>
              <h4 className={classes.orangeText}>Bid Alerts</h4>
              <h4 className={classes.italicText}>
                Use the below dropdown to set your preferences with regards to bid confirmation. If
                you enable this, you will always receive a confirmation pop up when placing a bid.
              </h4>
              <BiddingPreferences />
            </>
          )}

          {tabIndex === "login-history" && (
            <>
              <h4 style={{ fontWeight: 400, fontSize: "16px" }}>
                This page can be used to view your login history.
              </h4>
              <LoginHistory bidder={context.bidderState.bidder} />
            </>
          )}

          {tabIndex === "suspensions" && (
            <>
              <SuspensionDetails suspensions={context.bidderState.bidder.suspensionDetails} />
            </>
          )}

          {tabIndex === "payments" && (
            <>
              <PaymentDetails bidder={context.bidderState.bidder} />
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default MyAccountContainer;
